import { Component, EventEmitter, Input, Output, OnInit, signal, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { ParkingSite } from '../../../../../core/models/parking/parking-site.model';
import { ApiParkingSiteService } from '../../../../../core/services/api/parking/api-parking-site.service';
import { MapperParkingSiteService } from '../../../../../core/mappers/parking/mapper-parking-site.service';
import { MenuPermissionService } from '../../../../../core/services/menu-permission.service';
import { ModalAdminActivityHistoryComponent } from '../../system-settings/modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../../core/models/system-settings/admin-activity-log.model';

@Component({
  selector: 'app-modal-parking-site',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalAdminActivityHistoryComponent],
  templateUrl: './modal-parking-site.component.html',
  styleUrl: './modal-parking-site.component.css',
})
export class ModalParkingSiteComponent implements OnInit {
  @Input() itemId = 0;
  @Output() close = new EventEmitter<void>();
  @Output() saveSuccess = new EventEmitter<void>();

  readonly activityEntityTypes = AdminActivityEntityTypes;
  showActivityHistory = false;
  formData!: ParkingSite;
  isLoading = signal(false);
  isSaving = signal(false);

  private readonly toast = inject(ToastService);
  private readonly apiService = inject(ApiParkingSiteService);
  private readonly mapper = inject(MapperParkingSiteService);
  private readonly cookieService = inject(CookieService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  get isEdit(): boolean {
    return this.itemId > 0;
  }

  get logoPreviewSrc(): string | null {
    return this.mapper.toLogoSrc(this.formData.logoBase64);
  }

  get googleMapsUrl(): string | null {
    if (this.formData.latitude == null || this.formData.longitude == null) {
      return null;
    }
    return `https://www.google.com/maps?q=${this.formData.latitude},${this.formData.longitude}`;
  }

  ngOnInit(): void {
    this.formData = this.mapper.createEmpty();
    if (this.isEdit) {
      this.loadDetail();
    }
  }

  onClose(): void {
    this.close.emit();
  }

  openGoogleMaps(): void {
    const url = this.googleMapsUrl;
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  onLogoFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      this.toast.error('กรุณาเลือกไฟล์รูปภาพ');
      input.value = '';
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      this.toast.error('ขนาดไฟล์ต้องไม่เกิน 2MB');
      input.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      this.formData.logoBase64 = result.includes(',') ? result.split(',')[1] : result;
      this.formData.hasLogo = !!this.formData.logoBase64;
    };
    reader.readAsDataURL(file);
  }

  clearLogo(): void {
    this.formData.logoBase64 = '';
    this.formData.hasLogo = false;
  }

  onSave(): void {
    if (!this.formData.name?.trim()) {
      this.toast.error('กรุณาระบุชื่อ Site');
      return;
    }

    if (this.isEdit && !this.permissionService.canUpdate(this.router.url)) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }
    if (!this.isEdit && !this.permissionService.canCreate(this.router.url)) {
      this.toast.error('ไม่มีสิทธิ์สร้าง');
      return;
    }

    const userIdStr = this.cookieService.get('user_id');
    const userId = userIdStr ? parseInt(userIdStr, 10) : 0;
    const menuId = this.permissionService.getMenuId(this.router.url);

    if (!userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลผู้ใช้หรือสิทธิ์เมนู');
      return;
    }

    this.isSaving.set(true);
    const payload = this.mapper.mapToPayload(this.formData, userId, menuId);
    const request$ = this.isEdit
      ? this.apiService.updateParkingSite(payload, this.itemId)
      : this.apiService.insertParkingSite(payload);

    request$.subscribe({
      next: (res) => {
        this.isSaving.set(false);
        if (res?.isSuccess) {
          this.toast.success(this.isEdit ? 'แก้ไข Site เรียบร้อย' : 'สร้าง Site เรียบร้อย');
          this.saveSuccess.emit();
          this.close.emit();
        } else {
          this.toast.error(res?.message || 'บันทึกไม่สำเร็จ');
        }
      },
      error: (err) => {
        console.error(err);
        this.isSaving.set(false);
        this.toast.error('เกิดข้อผิดพลาดในการเชื่อมต่อเซิร์ฟเวอร์');
      },
    });
  }

  private loadDetail(): void {
    this.isLoading.set(true);
    this.apiService.getParkingSiteById(this.itemId).subscribe({
      next: (dto) => {
        this.formData = this.mapper.mapFromDto(dto);
        this.isLoading.set(false);
      },
      error: (err) => {
        console.error(err);
        this.toast.error('ไม่สามารถโหลดรายละเอียด Site ได้');
        this.isLoading.set(false);
        this.onClose();
      },
    });
  }

  get canShowActivityHistory(): boolean {
    return this.itemId > 0;
  }

  openActivityHistory(): void {
    if (!this.canShowActivityHistory) return;
    this.showActivityHistory = true;
  }

  closeActivityHistory(): void {
    this.showActivityHistory = false;
  }
}
